import { useEffect, useState } from 'react';

import { Button, Dialog, DialogProps } from 'ui-kit';
import toast from 'react-hot-toast';

import { VarianceCode, VarianceStatus } from '__generated__/globalTypes';
import { useUpdateRemittanceVarianceStatus } from './updateRemittanceVarianceStatus_gql';
import type { AccountVariance } from './getAccountRemittanceVariances_gql';
import VarianceCodeBadge, { VARIANCE_LABELS } from './VarianceCodeBadge';
import { useVarianceArrowSwitch } from './useVarianceArrowSwitch';

const NOTE_MAX_CHARS = 4000;

interface VarianceNoteDialogProps {
  dialog: DialogProps;
  accountId: string;
  variances: AccountVariance[];
  focusedVariance: AccountVariance | null;
  /** Called when the seller clicks a sibling badge or presses ←/→ to advance. Parent
   *  re-resolves the focused variance from the live `variancesByAccountId` map so the
   *  modal sees post-save `version` / `note` / `status` immediately after a save. */
  onFocusChange: (code: VarianceCode) => void;
  /** Called after a successful save so the parent can refetch the list. Awaited
   *  before re-enabling action buttons so a quick reopen sees fresh `version` /
   *  `note` / `status` instead of the stale snapshot that opened the dialog. */
  onSaved: () => Promise<void> | void;
}

const VarianceNoteDialog = ({
  dialog,
  accountId,
  variances,
  focusedVariance,
  onFocusChange,
  onSaved,
}: VarianceNoteDialogProps): JSX.Element | null => {
  const serverNote = focusedVariance?.note ?? '';
  const [noteText, setNoteText] = useState<string>(serverNote);
  // Set to the text just sent to the server after a successful save. Prevents the
  // transient dirty=true window between setPending(false) and the parent's useMemo
  // re-resolving focusedVariance from the freshly refetched variancesByAccountId.
  // Cleared when the parent catches up (serverNote changes → seeding effect fires).
  const [justSavedNote, setJustSavedNote] = useState<string | null>(null);

  // Seed the textarea from the server copy on open and whenever the focused code changes.
  // Keyed on variance_code, not serverNote: two codes can share the same note text (e.g.
  // both empty), so keying on the value would skip the reseed on such a switch and leave
  // the prior code's text — an Acknowledge could then persist the wrong code's note.
  // serverNote is read but intentionally not a dep: save/ack keep the modal open, so a
  // post-save refetch updates serverNote while the seller may be typing the next edit —
  // resetting on that change would silently discard the in-progress text.
  useEffect(() => {
    if (dialog.isOpen) {
      setNoteText(serverNote);
      setJustSavedNote(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.isOpen, focusedVariance?.variance_code]);

  // justSavedNote bridges the post-save gap until the parent's refetch lands the new
  // serverNote. Once serverNote catches up, drop the guard so dirty tracks serverNote
  // again. This only clears the guard — it never touches noteText, so it cannot clobber
  // a fresh in-progress edit.
  useEffect(() => {
    if (justSavedNote !== null && serverNote === justSavedNote) {
      setJustSavedNote(null);
    }
  }, [serverNote, justSavedNote]);

  const [updateStatus] = useUpdateRemittanceVarianceStatus();
  // Local pending spans the full save cycle (mutation → parent refetch → button re-enable).
  // useMutation's `loading` drops to false as soon as the mutation resolves, which would
  // re-enable the action buttons during the refetch window — a double-click there fires
  // a second mutation with the stale `focusedVariance.version` and trips the optimistic-lock.
  const [pending, setPending] = useState(false);

  const dirty = noteText !== (justSavedNote ?? serverNote);
  const remaining = NOTE_MAX_CHARS - noteText.length;
  const isAcknowledged = focusedVariance?.status === VarianceStatus.ACKNOWLEDGED;

  const canSwitch = !dirty && !pending;

  const switchTo = (code: VarianceCode) => {
    if (!canSwitch) return;
    if (focusedVariance && code === focusedVariance.variance_code) return;
    onFocusChange(code);
  };

  useVarianceArrowSwitch({
    enabled: dialog.isOpen && canSwitch,
    variances,
    focusedCode: focusedVariance?.variance_code ?? null,
    onSwitch: onFocusChange,
  });

  if (!focusedVariance) return null;

  const performUpdate = async (nextStatus: VarianceStatus) => {
    if (pending) return;
    setPending(true);
    try {
      await updateStatus({
        variables: {
          input: {
            upload_id: focusedVariance.upload_id,
            account_id: focusedVariance.account_id,
            variance_code: focusedVariance.variance_code,
            status: nextStatus,
            note: noteText,
            expected_version: focusedVariance.version,
          },
        },
      });
      try {
        await onSaved();
        // Refetch succeeded. Bridge the gap until the parent's re-render lands the fresh
        // focusedVariance so `dirty` doesn't flip true against the still-stale serverNote.
        // Setting this only after a successful refetch means a failed refetch never masks
        // stale state — otherwise the guard would hold `dirty` false while our cached
        // `version` is stale, tripping the optimistic lock on the next save.
        setJustSavedNote(noteText);
      } catch (refetchErr: unknown) {
        // Mutation persisted but the refetch failed, so our cached version/note/status is
        // now stale and any follow-up save would fail the optimistic lock. Close the modal
        // so reopening pulls fresh data instead of stranding the user on a stale snapshot.
        toast.error('Variance saved, but refreshing failed — reopen to continue.');
        dialog.setIsOpen(false);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to save variance: ${msg || 'unknown error'}`);
    } finally {
      setPending(false);
    }
  };

  const handleSave = () => performUpdate(focusedVariance.status);
  const handleAcknowledge = () => performUpdate(VarianceStatus.ACKNOWLEDGED);
  const handleCancel = () => {
    // Reset to the last known saved value, preferring justSavedNote over serverNote. A
    // cancel during the post-save refetch window must restore the just-saved text, not the
    // stale server copy — otherwise the field shows empty and a follow-up Acknowledge could
    // persist an empty note over what was just saved. Keeping justSavedNote leaves dirty
    // false; the seeding effect clears it once the refreshed serverNote catches up.
    setNoteText(justSavedNote ?? serverNote);
  };

  return (
    <Dialog dialog={dialog}>
      <button
        className="absolute right-[10px] top-[10px] text-gray-500 hover:text-gray-300"
        onClick={() => dialog.setIsOpen(false)}
        aria-label="Close"
      >
        ✕
      </button>
      <h2 className="mb-1 text-xl font-semibold">
        Variance — {VARIANCE_LABELS[focusedVariance.variance_code]}
      </h2>
      <div className="text-sm text-gray-400">
        Account {accountId} · {focusedVariance.payment_month}
      </div>
      <div className="mt-4 flex gap-4">
        <div className="flex w-48 flex-shrink-0 flex-col gap-2">
          <div className="text-xs uppercase tracking-wide text-gray-400">Detected codes</div>
          {variances.map((v) => (
            <VarianceCodeBadge
              key={v.variance_code}
              code={v.variance_code}
              focused={v.variance_code === focusedVariance.variance_code}
              acknowledged={v.status === VarianceStatus.ACKNOWLEDGED}
              disabled={!canSwitch}
              onSelect={switchTo}
            />
          ))}
          {variances.length > 1 && (
            <div className="mt-1 text-[10px] text-gray-500">←/→ to switch</div>
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <label
            htmlFor="variance-note"
            className="mb-1 text-xs uppercase tracking-wide text-gray-400"
          >
            Note
          </label>
          <textarea
            id="variance-note"
            className="h-32 w-full rounded-md border border-pink-400 bg-background-surface p-2 text-sm text-foreground-default focus:border-pink-400 focus:outline-none"
            value={noteText}
            maxLength={NOTE_MAX_CHARS}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Optional note explaining the cause of this variance"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-400">
            <span>{isAcknowledged ? 'Acknowledged' : 'Pending'}</span>
            <span>{remaining} chars remaining</span>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-between gap-2">
        <Button
          type="secondary"
          label="cancel-variance-note"
          size="medium"
          disabled={!dirty || pending}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <div className="flex gap-2">
          <Button
            type="secondary"
            label="save-variance-note"
            size="medium"
            disabled={!dirty || pending}
            onClick={handleSave}
          >
            Save Note
          </Button>
          <Button
            label="acknowledge-variance"
            size="medium"
            disabled={isAcknowledged || pending}
            onClick={handleAcknowledge}
          >
            {isAcknowledged ? 'Acknowledged' : 'Acknowledge'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default VarianceNoteDialog;
